import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  IconButton,
  Alert,
  CircularProgress,
  useTheme,
  Paper,
  Grid,
  LinearProgress,
  Avatar,
  Divider,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Close as CloseIcon,
  Help as HelpIcon,
  Delete as DeleteIcon,
  CloudUpload as CloudUploadIcon,
  VideoCall as VideoCallIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  Warning as WarningIcon,
  Send as SendIcon,
  Chat as ChatIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import axios from "axios";
import { useSocket } from "../../Hooks/SocketProvider";
import { BASE_URL } from "../../../services/configUrls";

const TicketModal = ({ open, onClose }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const [formData, setFormData] = useState({
    subject: "",
    description: "",
    attachments: [],
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [meetingLoading, setMeetingLoading] = useState(false);
  const [meetings, setMeetings] = useState([]);
  const [hasPendingMeetings, setHasPendingMeetings] = useState(false);
  const [fetchingMeetings, setFetchingMeetings] = useState(false);
  const [isDraftMode, setIsDraftMode] = useState(false);
  const [currentDraft, setCurrentDraft] = useState(null);

  // States for attachment handling
  const [uploadedAttachments, setUploadedAttachments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [fetchingAttachments, setFetchingAttachments] = useState(false);
  const [ticketHistory, setTicketHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);

  // States for delete confirmation
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState({
    open: false,
    attachment: null,
    isDeleting: false,
  });

  // Chat states
  const [activeTab, setActiveTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [chatStaffMeetingId, setChatStaffMeetingId] = useState(null);

  const [ticketTab, setTicketTab] = useState(0);

  const messagesEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const { socket, lastMessage, userId, connectionStatus } = useSocket() || {};
  

  const messagesRef = useRef(messages);
  const chatStaffMeetingIdRef = useRef(null);
  const isChatOpenRef = useRef(false);
  

  // API Configuration

  // Get access token from localStorage
  const getAccessToken = () => {
    try {
      const token =
        localStorage.getItem("accessToken") ||
        localStorage.getItem("access_token") ||
        localStorage.getItem("authToken");
      return (
        token ||
        "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI3NjYxZWEwMi1hYzIwLTQ1ZTAtYTVkNC02NjM5ZDgwMzg0ZjAiLCJleHAiOjE3NjI0MTk5OTF9.2lttCc7crkcLDPPD5ocqB1rlECYYahA34x7EzEOr0lA"
      );
    } catch (error) {
      console.error("Error getting access token:", error);
      return "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiI3NjYxZWEwMi1hYzIwLTQ1ZTAtYTVkNC02NjM5ZDgwMzg0ZjAiLCJleHAiOjE3NjI0MTk5OTF9.2lttCc7crkcLDPPD5ocqB1rlECYYahA34x7EzEOr0lA";
    }
  };

  // ============ CHAT FUNCTIONS ============

  // **Update refs whenever state changes**
  useEffect(() => {
  if (open) {
    setTicketTab(0);
  }
}, [open]);


  useEffect(() => {
    if (ticketTab === 1) {
      setLoading(true);
      setError(null);

      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/spoc_meeting_history`,
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      };

      axios
        .request(config)
        .then((response) => {
          setTicketHistory(response.data);
          setLoading(false);
        })
        .catch((error) => {
          setError("Failed to load ticket history");
          setLoading(false);
        });
    }
  }, [ticketTab]);

  useEffect(() => {
    messagesRef.current = messages;
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    chatStaffMeetingIdRef.current = chatStaffMeetingId;
    console.log("📌 Meeting ID updated:", chatStaffMeetingId);
  }, [chatStaffMeetingId]);

  useEffect(() => {
    isChatOpenRef.current = activeTab === 1;
    console.log("📌 Chat open status:", activeTab === 1);
  }, [activeTab]);

  // Scroll to bottom when messages change
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // Fetch chat messages
  const fetchChatMessages = async (staffMeetingId) => {
    if (!staffMeetingId) {
      console.warn("No staffMeetingId provided");
      return;
    }

    console.log("=== FETCHING CHAT MESSAGES ===");
    console.log("Meeting ID:", staffMeetingId);
    console.log(
      "API URL:",
      `${BASE_URL}/staffmetting/staffMeetingChat/${staffMeetingId}`
    );

    setIsLoadingMessages(true);
    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/staffMeetingChat/${staffMeetingId}`,
        headers: {
          Authorization: `Bearer ${getAccessToken()}`,
        },
      };

      console.log("Making API request...");
      const response = await axios.request(config);
      console.log("Chat response:", response.data);

      if (response.data && response.data.messages) {
        const formattedMessages = response.data.messages.map((msg) => ({
          id: msg.id,
          message: msg.message,
          role: msg.is_spoc === "1" ? "staff" : "eduskills",
          is_spoc: msg.is_spoc,
          created_at: msg.created_at,
          made_by_user_id: msg.made_by_user_id,
          staff_meting_id: msg.staff_meting_id || staffMeetingId,
        }));

        console.log("Formatted messages count:", formattedMessages.length);
        setMessages(formattedMessages);
      } else {
        console.warn("No messages in response");
        setMessages([]);
      }
    } catch (error) {
      console.error("Error fetching chat messages:", error);
      console.error("Error status:", error.response?.status);
      console.error("Error data:", error.response?.data);

      if (error.response?.status === 404) {
        console.log("No messages found - showing empty state");
        setMessages([]);
      } else if (error.response?.status === 401) {
        setError("Authentication failed. Please login again.");
      } else {
        setError("Failed to load messages. Please try again.");
      }
    } finally {
      setIsLoadingMessages(false);
      console.log("=== FETCH COMPLETE ===");
    }
  };

  // Auto-fetch messages when chatStaffMeetingId or activeTab changes
  useEffect(() => {
    if (chatStaffMeetingId && activeTab === 1) {
      console.log("Auto-fetching messages for meeting:", chatStaffMeetingId);
      fetchChatMessages(chatStaffMeetingId);
    }
  }, [chatStaffMeetingId, activeTab]);

  // **FIXED: WebSocket listener that catches messages from ALL users**
  useEffect(() => {
    if (!socket) {
      console.log("⚠️ No socket available");
      return;
    }

    const handleWebSocketMessage = (event) => {
      console.log("📩 ========== NEW WEBSOCKET MESSAGE ==========");
      console.log("📩 Raw event data:", event.data);

      try {
        // Robust parser that handles multiple levels of stringification
        const parseMessage = (data) => {
          let parsed = data;
          let attempts = 0;
          const maxAttempts = 3;

          while (typeof parsed === "string" && attempts < maxAttempts) {
            try {
              const nextParse = JSON.parse(parsed);
              parsed = nextParse;
              attempts++;
              console.log(
                `🔍 Parse attempt ${attempts}:`,
                JSON.stringify(parsed, null, 2)
              );
            } catch (e) {
              console.log("⚠️ Parse stopped at attempt", attempts);
              break;
            }
          }

          return parsed;
        };

        const messageData = parseMessage(event.data);
        console.log(
          "✅ Final parsed data:",
          JSON.stringify(messageData, null, 2)
        );

        const isChatOpen = isChatOpenRef.current;
        const currentMeetingId = chatStaffMeetingIdRef.current;

        console.log("📊 Current state:", {
          isChatOpen,
          currentMeetingId,
          hasStatus: messageData.status,
          hasChatResponse: !!messageData.chat_response,
        });

        // **CRITICAL: Check for exact response structure from backend**
        if (messageData.chat_response) {
          const chatResponse = messageData.chat_response;
          const messageMeetingId =
            chatResponse.staff_meting_id || chatResponse.staff_meeting_id;

          console.log("🔍 Comparing:", {
            messageMeetingId,
            currentMeetingId,
            match: messageMeetingId == currentMeetingId,
            isSpoc: chatResponse.is_spoc,
            messageFrom: chatResponse.made_by_user_id,
            currentUser: userId,
          });

          // **FIX: Accept messages for current meeting from ALL users**
          if (
            isChatOpen &&
            currentMeetingId &&
            messageMeetingId == currentMeetingId
          ) {
            console.log("✅ ✅ ✅ ACCEPTING MESSAGE FOR CURRENT MEETING");

            setMessages((prevMessages) => {
              console.log("📋 Previous messages count:", prevMessages.length);

              // Only filter "sending" messages if it's from current user
              const isOwnMessage =
                chatResponse.made_by_user_id == userId ||
                chatResponse.made_by_userid == userId;

              let filteredMessages = prevMessages;
              if (isOwnMessage) {
                filteredMessages = prevMessages.filter((msg) => !msg.sending);
                console.log("🗑️ Filtered out sending messages (own message)");
              }

              // Check if message already exists (avoid duplicates)
              const messageExists = filteredMessages.some(
                (msg) =>
                  msg.id === chatResponse.id ||
                  (msg.message === chatResponse.message &&
                    msg.created_at === chatResponse.created_at)
              );

              if (messageExists) {
                console.log("⚠️ Message already exists, skipping");
                return filteredMessages;
              }

              console.log("➕ Adding new message to UI:", {
                id: chatResponse.id,
                message: chatResponse.message,
                is_spoc: chatResponse.is_spoc,
                made_by_user_id: chatResponse.made_by_user_id,
                from:
                  chatResponse.is_spoc === "1" ? "Staff (You)" : "EduSkills",
              });

              // Add the new message
              return [...filteredMessages, chatResponse];
            });
          } else {
            console.log("❌ MESSAGE REJECTED:", {
              chatOpen: isChatOpen,
              hasMeetingId: !!currentMeetingId,
              idsMatch: messageMeetingId == currentMeetingId,
              reason: !isChatOpen
                ? "Chat not open"
                : !currentMeetingId
                ? "No meeting ID set"
                : messageMeetingId != currentMeetingId
                ? "Different meeting ID"
                : "Unknown reason",
            });
          }
        } else {
          console.log("⚠️ No chat_response found in message data");
        }
      } catch (error) {
        console.error("💥 Error in WebSocket handler:", error);
        console.error("Error stack:", error.stack);
      }
      console.log("📩 ========== END MESSAGE PROCESSING ==========\n");
    };

    console.log("🔌 Adding WebSocket listener");
    socket.addEventListener("message", handleWebSocketMessage);

    return () => {
      console.log("🔌 Removing WebSocket listener");
      socket.removeEventListener("message", handleWebSocketMessage);
    };
  }, [socket, userId]);

  // Send message - UPDATED: Staff sends with is_spoc: "1"
  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      console.warn("Cannot send empty message");
      return;
    }

    if (!socket || socket.readyState !== WebSocket.OPEN) {
      console.error("Socket not available or not open");
      setError("WebSocket not connected. Please refresh the page.");
      return;
    }

    if (!chatStaffMeetingId) {
      console.error("No meeting ID available");
      setError("Meeting ID not found. Please try again.");
      return;
    }

    console.log("=== SENDING MESSAGE ===");
    console.log("Message:", newMessage.trim());
    console.log("Meeting ID:", chatStaffMeetingId);

    setIsSending(true);

    try {
      const messageData = {
        staff_meting_id: chatStaffMeetingId,
        message: newMessage.trim(),
        made_by_user_id: userId,
        role: "staff",
        is_spoc: "1", // CHANGED: Staff sends with is_spoc: "1"
      };

      console.log("Sending message data:", messageData);

      // Add optimistic update
      const tempMessage = {
        id: `temp-${Date.now()}`,
        message: newMessage.trim(),
        role: "staff",
        is_spoc: "1", // CHANGED: is_spoc: "1" for staff
        created_at: new Date().toISOString(),
        made_by_user_id: userId,
        staff_meting_id: chatStaffMeetingId,
        sending: true,
      };

      setMessages((prev) => [...prev, tempMessage]);

      // Send via WebSocket
      socket.send(JSON.stringify(messageData));
      console.log("Message sent via WebSocket");

      // Clear input field
      setNewMessage("");
    } catch (error) {
      console.error("Error sending message:", error);
      setError("Failed to send message. Please try again.");

      // Remove failed message from UI
      setMessages((prev) => prev.filter((msg) => !msg.sending));
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Format timestamp
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Format date for grouping
  const formatChatDate = (date) => {
    const messageDate = new Date(date);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (messageDate.toDateString() === today.toDateString()) {
      return "Today";
    } else if (messageDate.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    } else {
      return messageDate.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    }
  };

  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};
    messages.forEach((msg) => {
      const dateKey = formatChatDate(msg.created_at);
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(msg);
    });
    return groups;
  };

  // Get message sender info - CORRECTED: is_spoc 1 = You, is_spoc 0 = EduSkills
  const getMessageSenderInfo = (message) => {
    const isSpoc = message.is_spoc === "1" || message.is_spoc === 1;
    const isOwnMessage = message.made_by_user_id == userId;

    return {
      isSpoc,
      isOwnMessage,
      // CORRECTED: is_spoc 1 = You (Staff), is_spoc 0 = EduSkills (Support)
      label: isSpoc ? "You" : "EduSkills",
      color: isSpoc ? "#4ade80" : "#0A507D",
      bgColor: isSpoc ? "#d1fae5" : "#dbeafe",
      avatarBg: isSpoc ? "#4ade80" : "#0A507D",
      avatarText: isSpoc ? "ME" : "ES",
    };
  };

  // Open chat for a meeting
  const handleOpenChat = (meetingId) => {
    console.log("=== OPENING CHAT ===");
    console.log("Meeting ID:", meetingId);
    console.log("Connection Status:", connectionStatus);

    // Clear previous messages first
    setMessages([]);
    setChatStaffMeetingId(meetingId);
    setActiveTab(1);
  };

  // ============ EXISTING TICKET FUNCTIONS ============

  // Fetch meetings from API
  const fetchMeetings = async () => {
    const token = getAccessToken();

    if (!token) {
      setError("Authentication token not found. Please login again.");
      return;
    }

    setFetchingMeetings(true);
    setError("");

    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/get_rised_meeting_2`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.request(config);
      console.log("API Response:", response.data);

      if (response.data) {
        const meetingData = response.data;
        const meetings = Array.isArray(meetingData)
          ? meetingData
          : [meetingData];

        if (meetings.length > 0) {
          setMeetings(meetings);

          const pendingMeetings = meetings.filter(
            (meeting) =>
              meeting.closing_message === null ||
              meeting.message === "you have a pending ticket"
          );

          if (pendingMeetings.length > 0) {
            console.log("Pending meeting found:", pendingMeetings[0]);

            if (pendingMeetings[0].subjects && pendingMeetings[0].description) {
              setFormData({
                subject: pendingMeetings[0].subjects || "",
                description: pendingMeetings[0].description || "",
                attachments: [],
              });
              setCurrentDraft(pendingMeetings[0]);
              setIsDraftMode(true);
              setHasPendingMeetings(true);

              if (pendingMeetings[0].support_meeting_id) {
                setChatStaffMeetingId(pendingMeetings[0].support_meeting_id);
              }

              fetchStaffMeetingAttachments();
            } else {
              setHasPendingMeetings(true);
              clearFormData();
            }
          } else {
            setHasPendingMeetings(false);
            clearFormData();
          }
        } else {
          setMeetings([]);
          setHasPendingMeetings(false);
          clearFormData();
        }
      } else {
        setMeetings([]);
        setHasPendingMeetings(false);
        clearFormData();
      }
    } catch (error) {
      console.error("Error fetching meetings:", error);

      if (error.response?.status === 401) {
        setError("Authentication failed. Please login again.");
        localStorage.removeItem("accessToken");
      } else {
        setError("Failed to load support meetings. Please try again.");
      }

      setMeetings([]);
      setHasPendingMeetings(false);
      clearFormData();
    } finally {
      setFetchingMeetings(false);
    }
  };

  // Fetch staff meeting attachments
  const fetchStaffMeetingAttachments = async () => {
    const token = getAccessToken();

    if (!token) {
      console.error("Authentication token not found");
      return;
    }

    setFetchingAttachments(true);

    try {
      const config = {
        method: "get",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/fetch_staff_meeting_attachments`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.request(config);
      console.log("Fetched attachments:", JSON.stringify(response.data));

      if (response.data && Array.isArray(response.data)) {
        const formattedAttachments = response.data.map((attachment) => ({
          id: attachment.staff_metting_attachment_id,
          staffmettingattachmentid: attachment.staff_metting_attachment_id,
          staffMettingId: attachment.staff_meting_id,
          fileName: attachment.stsff_metting_attachment_name
            ? attachment.stsff_metting_attachment_name.split("/").pop()
            : "Unknown file",
          url: attachment.stsff_metting_attachment_name,
          fileSize: 0,
          fileType: attachment.stsff_metting_attachment_name
            ? attachment.stsff_metting_attachment_name.split(".").pop()
            : "unknown",
          isExisting: true,
          isUploaded: true,
          uploadedAt: new Date().toISOString(),
        }));

        setExistingAttachments(formattedAttachments);
        setUploadedAttachments(formattedAttachments);

        console.log("Server attachments only:", formattedAttachments);
      }
    } catch (error) {
      console.error("Error fetching attachments:", error);
      if (error.response?.status !== 404) {
        setError("Failed to load existing attachments");
      }
    } finally {
      setFetchingAttachments(false);
    }
  };

  // Delete attachment from server
  const deleteAttachmentFromServer = async (attachmentId) => {
    const token = getAccessToken();

    if (!token) {
      throw new Error("Authentication token not found");
    }

    try {
      const config = {
        method: "delete",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/deleet_staff_meeting/${attachmentId}`,
        headers: {
          Authorization: `Bearer ${token}`,
        },
      };

      const response = await axios.request(config);
      console.log("Delete response:", JSON.stringify(response.data));

      return response.data;
    } catch (error) {
      console.error("Delete attachment error:", error);

      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 404:
            console.log("Attachment not found (may already be deleted)");
            return {
              success: true,
              message: "Attachment not found (may already be deleted)",
            };
          case 401:
            throw new Error("Authentication failed. Please login again.");
          case 403:
            throw new Error(
              "Access denied. You cannot delete this attachment."
            );
          case 500:
            throw new Error("Server error occurred while deleting attachment.");
          default:
            throw new Error(
              data?.message || `Delete failed with status ${status}`
            );
        }
      } else if (error.request) {
        throw new Error(
          "Network error. Please check your connection and try again."
        );
      } else {
        throw new Error(error.message || "Unknown error occurred");
      }
    }
  };

  // Handle delete attachment confirmation
  const handleDeleteConfirmation = (attachment) => {
    setDeleteConfirmDialog({
      open: true,
      attachment: attachment,
      isDeleting: false,
    });
  };

  // Handle confirm delete
  const handleConfirmDelete = async () => {
    const { attachment } = deleteConfirmDialog;

    if (!attachment) return;

    setDeleteConfirmDialog((prev) => ({
      ...prev,
      isDeleting: true,
    }));

    try {
      if (attachment.isExisting && attachment.staffmettingattachmentid) {
        await deleteAttachmentFromServer(attachment.staffmettingattachmentid);

        setSuccess("✅ Attachment deleted successfully!");
        setTimeout(() => setSuccess(""), 3000);

        setTimeout(() => {
          setExistingAttachments([]);
          setUploadedAttachments([]);
          fetchStaffMeetingAttachments();
        }, 500);
      } else {
        setUploadedAttachments((prev) =>
          prev.filter((att) => att.id !== attachment.id)
        );

        setUploadProgress((prev) => {
          const newProgress = { ...prev };
          delete newProgress[attachment.id];
          return newProgress;
        });
      }

      setDeleteConfirmDialog({
        open: false,
        attachment: null,
        isDeleting: false,
      });
    } catch (error) {
      console.error("Failed to delete attachment:", error);
      setError(
        `Failed to delete attachment: ${error.message || "Unknown error"}`
      );

      setDeleteConfirmDialog((prev) => ({
        ...prev,
        isDeleting: false,
      }));
    }
  };

  // Cancel delete
  const handleCancelDelete = () => {
    setDeleteConfirmDialog({
      open: false,
      attachment: null,
      isDeleting: false,
    });
  };

  // Upload file to server
  const uploadFileToServer = async (file, attachmentId) => {
    const token = getAccessToken();

    if (!token) {
      throw new Error("Authentication token not found");
    }

    const data = new FormData();
    data.append("attachment", file);

    try {
      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/add_saff_meeting_attachment`,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(data.getHeaders ? data.getHeaders() : {}),
        },
        data: data,
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          setUploadProgress((prev) => ({
            ...prev,
            [attachmentId]: percentCompleted,
          }));
        },
      };

      const response = await axios.request(config);
      console.log("Upload response:", response.data);

      return response.data;
    } catch (error) {
      console.error("Upload error:", error);
      throw error;
    }
  };

  // Handle file change
  const handleFileChange = async (event) => {
    const files = Array.from(event.target.files);
    const validFiles = [];
    const errors = [];

    files.forEach((file) => {
      const validation = validateFile(file);
      if (validation.isValid) {
        validFiles.push(file);
      } else {
        errors.push(validation.error);
      }
    });

    if (errors.length > 0) {
      setError(errors.join(", "));
      setTimeout(() => setError(""), 5000);
    }

    if (validFiles.length > 0) {
      setIsUploading(true);

      for (const file of validFiles) {
        const attachmentId = Date.now() + Math.random();

        const fileObject = {
          id: attachmentId,
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          file: file,
          uploadedAt: new Date().toISOString(),
          isExisting: false,
          isUploaded: false,
          isUploading: true,
        };

        setUploadedAttachments((prev) => [...prev, fileObject]);

        try {
          const serverResponse = await uploadFileToServer(file, attachmentId);

          setUploadedAttachments((prev) =>
            prev.filter((attachment) => attachment.id !== attachmentId)
          );

          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[attachmentId];
            return newProgress;
          });

          setSuccess(`✅ ${file.name} uploaded successfully!`);
          setTimeout(() => setSuccess(""), 3000);

          setTimeout(() => {
            fetchStaffMeetingAttachments();
          }, 500);
        } catch (error) {
          console.error("Failed to upload file:", error);

          setUploadedAttachments((prev) =>
            prev.map((attachment) =>
              attachment.id === attachmentId
                ? {
                    ...attachment,
                    isUploaded: false,
                    isUploading: false,
                    uploadError: error.message || "Upload failed",
                  }
                : attachment
            )
          );

          setUploadProgress((prev) => {
            const newProgress = { ...prev };
            delete newProgress[attachmentId];
            return newProgress;
          });

          setError(`Failed to upload ${file.name}: ${error.message}`);
        }
      }

      setIsUploading(false);
    }

    event.target.value = "";
  };

  // Schedule meeting
  const scheduleMeeting = async () => {
    const token = getAccessToken();

    if (!token) {
      setError("Authentication token not found. Please login again.");
      return;
    }

    if (!formData.subject || !formData.description) {
      setError(
        "Please fill in both subject and description before scheduling a meeting"
      );
      return;
    }

    setMeetingLoading(true);
    setError("");

    try {
      const data = new FormData();
      data.append("subjects", formData.subject);
      data.append("description", formData.description);

      const config = {
        method: "post",
        maxBodyLength: Infinity,
        url: `${BASE_URL}/staffmetting/rise_metting_by_4`,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(data.getHeaders ? data.getHeaders() : {}),
        },
        data: data,
      };

      const response = await axios.request(config);
      console.log("Meeting scheduled:", JSON.stringify(response.data));

      setSuccess(
        "🎉 Meeting scheduled successfully! You will receive a notification once it's confirmed."
      );

      setTimeout(() => {
        setSuccess("");
        clearFormData();
        fetchMeetings();
      }, 3000);
    } catch (error) {
      console.error("Error scheduling meeting:", error);

      if (error.response) {
        const { status, data } = error.response;

        switch (status) {
          case 401:
            setError("Authentication failed. Please login again.");
            localStorage.removeItem("accessToken");
            break;
          case 403:
            setError("Access denied. You cannot schedule meetings.");
            break;
          case 422:
            setError(
              `Validation error: ${data?.message || "Invalid meeting data"}`
            );
            break;
          case 500:
            setError("Server error occurred while scheduling meeting.");
            break;
          default:
            setError(
              data?.message || `Meeting scheduling failed with status ${status}`
            );
        }
      } else if (error.request) {
        setError("Network error. Please check your connection and try again.");
      } else {
        setError(error.message || "Unknown error occurred");
      }
    } finally {
      setMeetingLoading(false);
    }
  };

  // Handle file action
  const handleFileAction = (attachment) => {
    if (attachment.isExisting) {
      if (attachment.url) {
        try {
          const url = attachment.url.startsWith("http")
            ? attachment.url
            : new URL(attachment.url, BASE_URL);
          window.open(url, "_blank");
        } catch (error) {
          console.error("Invalid attachment URL:", error);
          setError("Invalid attachment URL. Cannot open file.");
        }
      } else {
        setError("Attachment URL not available.");
      }
    } else {
      handleDeleteConfirmation(attachment);
    }
  };

  // Clear form data
  const clearFormData = () => {
    setFormData({
      subject: "",
      description: "",
      attachments: [],
    });
    setUploadedAttachments([]);
    setExistingAttachments([]);
    setIsDraftMode(false);
    setCurrentDraft(null);
    setUploadProgress({});
    setDeleteConfirmDialog({
      open: false,
      attachment: null,
      isDeleting: false,
    });
  };

  // useEffect
  useEffect(() => {
    if (open) {
      const token = getAccessToken();
      if (token) {
        setUploadedAttachments([]);
        setExistingAttachments([]);
        fetchMeetings();
      } else {
        setError("Please login to access support tickets.");
      }
    } else {
      clearFormData();
      setError("");
      setSuccess(false);
      setMessages([]);
      setChatStaffMeetingId(null);
      setActiveTab(0);
    }
  }, [open]);

  // File validation
  const validateFile = (file) => {
    const maxSize = 2 * 1024 * 1024;
    const allowedTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "image/jpeg",
      "image/jpg",
      "image/png",
      "text/plain",
    ];

    if (file.size > maxSize) {
      return {
        isValid: false,
        error: `${file.name} exceeds the 2MB size limit`,
      };
    }

    if (!allowedTypes.includes(file.type)) {
      return {
        isValid: false,
        error: `${file.name} is not a supported file type`,
      };
    }

    return { isValid: true };
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return "Unknown size";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  // Get file icon based on type
  const getFileIcon = (fileName, fileType) => {
    if (!fileName) return "📎";
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (
      ["jpg", "jpeg", "png", "gif"].includes(ext) ||
      fileType?.startsWith("image/")
    ) {
      return "🖼️";
    } else if (["pdf"].includes(ext) || fileType === "application/pdf") {
      return "📄";
    } else if (["doc", "docx"].includes(ext)) {
      return "📝";
    } else if (["txt"].includes(ext)) {
      return "📄";
    }
    return "📎";
  };

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
    setError("");
  };

  const handleJoinMeeting = (meeting) => {
    console.log("🔗 Attempting to join meeting:", meeting);

    // Support multiple possible field name variations from API
    const meetingLink =
      meeting.meetig_link || meeting.mettinglink || meeting.meeting_link;

    if (!meetingLink) {
      console.error("❌ No meeting link found in:", meeting);
      setError(
        "Meeting link is not available yet. Please wait for the meeting to be scheduled."
      );
      setTimeout(() => setError(""), 4000);
      return;
    }

    // Validate URL format
    try {
      const url = new URL(meetingLink);
      console.log("✅ Opening meeting link:", url.href);
      window.open(url.href, "_blank");
    } catch (error) {
      console.error("❌ Invalid meeting link:", meetingLink, error);
      setError("Invalid meeting link format. Please contact support.");
      setTimeout(() => setError(""), 4000);
    }
  };

  const handleClose = () => {
    clearFormData();
    setError("");
    setSuccess(false);
    setMeetings([]);
    setHasPendingMeetings(false);
    setFetchingMeetings(false);
    setMessages([]);
    setChatStaffMeetingId(null);
    setActiveTab(0);
    onClose();
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const messageGroups = groupMessagesByDate(messages);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 4,
            bgcolor: isDarkMode ? "#1e1e1e" : "#ffffff",
            boxShadow: isDarkMode
              ? "0 20px 25px -5px rgba(0,0,0,0.6), 0 10px 10px -5px rgba(0,0,0,0.4)"
              : "0 20px 25px -5px rgba(0,0,0,0.1), 0 10px 10px -5px rgba(0,0,0,0.04)",
            overflow: "hidden",
            height: "80vh",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 2,
            background: "#0A507D",
            color: "white",
            position: "relative",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 3,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backdropFilter: "blur(10px)",
              }}
            >
              {activeTab === 1 ? (
                <ChatIcon sx={{ fontSize: 24 }} />
              ) : isDraftMode ? (
                <EditIcon sx={{ fontSize: 24 }} />
              ) : (
                <HelpIcon sx={{ fontSize: 24 }} />
              )}
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
                {activeTab === 1
                  ? "Eduskills Support"
                  : isDraftMode
                  ? "Eduskills Support"
                  : "Eduskills Support"}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {activeTab === 1 && (
                  <>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor:
                          connectionStatus === "connected"
                            ? "#4ade80"
                            : "#ef4444",
                        animation:
                          connectionStatus === "connected"
                            ? "pulse 2s infinite"
                            : "none",
                        "@keyframes pulse": {
                          "0%, 100%": { opacity: 1 },
                          "50%": { opacity: 0.5 },
                        },
                      }}
                    />
                    <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                      {connectionStatus === "connected"
                        ? "Online"
                        : connectionStatus === "connecting"
                        ? "Connecting..."
                        : "Offline"}
                    </Typography>
                  </>
                )}
                {activeTab === 0 && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    {fetchingMeetings
                      ? "Loading your support meetings..."
                      : isDraftMode
                      ? `Continue editing "${formData.subject || "your draft"}"`
                      : hasPendingMeetings
                      ? "Your support meetings"
                      : "We're here to help you"}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "white",
              bgcolor: "rgba(255,255,255,0.1)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* Tabs */}
        {hasPendingMeetings && (
          <Box
            sx={{
              borderBottom: 1,
              borderColor: isDarkMode ? "#404040" : "divider",
              bgcolor: isDarkMode ? "#1e1e1e" : "#ffffff",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => {
                setActiveTab(newValue);
                // Re-fetch messages when switching to chat tab
                if (newValue === 1 && chatStaffMeetingId) {
                  fetchChatMessages(chatStaffMeetingId);
                }
              }}
              sx={{
                "& .MuiTab-root": {
                  color: isDarkMode ? "#b0bec5" : "#666",
                  "&.Mui-selected": {
                    color: "#0A507D",
                  },
                },
              }}
            >
              <Tab label="Ticket Details" />
              <Tab
                label="Chat"
                icon={
                  connectionStatus === "connected" ? (
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        bgcolor: "#4ade80",
                        ml: 1,
                      }}
                    />
                  ) : null
                }
                iconPosition="end"
              />
            </Tabs>
          </Box>
        )}

        {/* Content */}
        <DialogContent
          sx={{
            flex: 1,
            overflow: "auto",
            p: activeTab === 1 ? 0 : 4,
            bgcolor: isDarkMode
              ? activeTab === 1
                ? "#121212"
                : "#1e1e1e"
              : activeTab === 1
              ? "#f5f7fa"
              : "#fafafa",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Ticket Form Tab */}
          {activeTab === 0 && (
            <>
              {error && (
                <Alert
                  severity="error"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-icon": { fontSize: 20 },
                  }}
                  onClose={() => setError("")}
                >
                  {error}
                </Alert>
              )}

              {success && (
                <Alert
                  severity="success"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-icon": { fontSize: 20 },
                  }}
                >
                  {typeof success === "string"
                    ? success
                    : `🎉 Support request ${
                        isDraftMode ? "updated" : "submitted"
                      } successfully!`}
                </Alert>
              )}
{/* 
              {isDraftMode && currentDraft && (
                <Alert
                  severity="info"
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-icon": { fontSize: 20 },
                  }}
                >
                  📝 Editing draft ticket created on{" "}
                  {formatDate(currentDraft.created_at)}
                  {currentDraft.updated_at !== currentDraft.created_at &&
                    ` (last updated: ${formatDate(currentDraft.updated_at)})`}
                </Alert>
              )} */}

              {fetchingMeetings && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <CircularProgress size={40} sx={{ mr: 2 }} />
                  <Typography
                    variant="body1"
                    sx={{ color: isDarkMode ? "#b0bec5" : "#666666" }}
                  >
                    Loading your support meetings...
                  </Typography>
                </Box>
              )}

              {!fetchingMeetings && hasPendingMeetings ? (
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      mb: 3,
                      fontWeight: 600,
                      color: isDarkMode ? "#ffffff" : "#333333",
                    }}
                  >
                    Your Active Support Sessions
                  </Typography>

                  {meetings
                    .filter((meeting) => meeting.closing_message === null)
                    .map((meeting) => (
                      <Paper
                        key={meeting.support_meeting_id}
                        elevation={0}
                        sx={{
                          p: 4,
                          mb: 3,
                          bgcolor: isDarkMode ? "#2d2d2d" : "#fff3cd",
                          border: `2px solid ${
                            isDarkMode ? "#ffc107" : "#ffeaa7"
                          }`,
                          borderRadius: 3,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            mb: 3,
                          }}
                        >
                          <ScheduleIcon
                            sx={{ color: "#ffc107", fontSize: 32 }}
                          />
                          <Box>
                            <Typography
                              variant="h6"
                              sx={{
                                fontWeight: 600,
                                color: isDarkMode ? "#ffffff" : "#333333",
                              }}
                            >
                              {meeting.subjects}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDarkMode ? "#b0bec5" : "#666666",
                                mt: 1,
                              }}
                            >
                              {meeting.description}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: isDarkMode ? "#b0bec5" : "#666666",
                                mt: 0.5,
                              }}
                            >
                              Status: {meeting.message} - Will be resolved
                              within 24 hours
                            </Typography>
                          </Box>
                        </Box>

                        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                          {/* <Button
                            variant="contained"
                            size="large"
                            startIcon={
                              meetingLoading ? (
                                <CircularProgress size={18} color="inherit" />
                              ) : (
                                <VideoCallIcon />
                              )
                            }
                            onClick={() => handleJoinMeeting(meeting)}
                            disabled={meetingLoading || !meeting.meetig_link}
                            sx={{
                              borderRadius: 3,
                              px: 4,
                              py: 1.5,
                              bgcolor: "#28a745",
                              boxShadow: "0 4px 15px 0 rgba(40, 167, 69, 0.4)",
                              "&:hover": {
                                bgcolor: "#218838",
                                boxShadow:
                                  "0 6px 20px 0 rgba(40, 167, 69, 0.6)",
                              },
                              "&:disabled": {
                                bgcolor: isDarkMode ? "#333" : "#e0e0e0",
                                color: isDarkMode ? "#666" : "#9ca3af",
                              },
                            }}
                          >
                            {meetingLoading ? "Connecting..." : "Join Meeting"}
                          </Button> */}

                          <Button
                            variant="outlined"
                            size="large"
                            startIcon={<ChatIcon />}
                            onClick={() =>
                              handleOpenChat(meeting.support_meeting_id)
                            }
                            sx={{
                              borderRadius: 3,
                              px: 3,
                              py: 1.5,
                              borderColor: "#0A507D",
                              color: "#0A507D",
                            }}
                          >
                            Open Chat
                          </Button>

                          {/* <Button
                            variant="outlined"
                            size="large"
                            onClick={fetchMeetings}
                            sx={{
                              borderRadius: 3,
                              px: 3,
                              py: 1.5,
                              borderColor: isDarkMode ? "#404040" : "#e0e0e0",
                              color: isDarkMode ? "#b0bec5" : "#666666",
                            }}
                          >
                            Refresh Status
                          </Button> */}
                        </Box>
                      </Paper>
                    ))}
                </Box>
              ) : !fetchingMeetings ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
                  <Tabs
                    value={ticketTab}
                    onChange={(e, newValue) => setTicketTab(newValue)}
                  >
                    <Tab label="Rise Ticket" />
                    <Tab label="Ticket History" />
                  </Tabs>
                  {ticketTab === 0 && (
                    <Box sx={{ mt: 2 }}>
                      {/* Subject */}

                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: isDarkMode ? "#ffffff" : "#333333",
                          }}
                        >
                          Subject *{" "}
                          {isDraftMode && (
                            <Typography
                              component="span"
                              sx={{ color: "#2196f3", fontSize: "0.8rem" }}
                            >
                              (from draft)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          fullWidth
                          value={formData.subject}
                          onChange={(e) =>
                            handleInputChange("subject", e.target.value)
                          }
                          placeholder="What can we help you with?"
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: isDarkMode ? "#2d2d2d" : "#ffffff",
                              borderRadius: 3,
                              ...(isDraftMode &&
                                formData.subject && {
                                  borderColor: "#2196f3",
                                  "& fieldset": {
                                    borderColor: "#2196f3",
                                    borderWidth: "2px",
                                  },
                                }),
                            },
                          }}
                        />
                      </Box>

                      {/* Description */}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: isDarkMode ? "#ffffff" : "#333333",
                          }}
                        >
                          Description *{" "}
                          {isDraftMode && (
                            <Typography
                              component="span"
                              sx={{ color: "#2196f3", fontSize: "0.8rem" }}
                            >
                              (from draft)
                            </Typography>
                          )}
                        </Typography>
                        <TextField
                          fullWidth
                          multiline
                          rows={5}
                          value={formData.description}
                          onChange={(e) =>
                            handleInputChange("description", e.target.value)
                          }
                          placeholder="Please describe your issue in detail."
                          variant="outlined"
                          sx={{
                            "& .MuiOutlinedInput-root": {
                              bgcolor: isDarkMode ? "#2d2d2d" : "#ffffff",
                              borderRadius: 3,
                              ...(isDraftMode &&
                                formData.description && {
                                  borderColor: "#2196f3",
                                  "& fieldset": {
                                    borderColor: "#2196f3",
                                    borderWidth: "2px",
                                  },
                                }),
                            },
                          }}
                        />
                      </Box>

                      {/* File Upload Section */}
                      <Box>
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mb: 1,
                            fontWeight: 600,
                            color: isDarkMode ? "#ffffff" : "#333333",
                          }}
                        >
                          Attachments (Optional)
                        </Typography>

                        {/* File Upload Area */}
                        <Paper
                          elevation={0}
                          sx={{
                            border: `2px dashed ${
                              isDarkMode ? "#404040" : "#e0e0e0"
                            }`,
                            borderRadius: 3,
                            bgcolor: isDarkMode ? "#2d2d2d" : "#ffffff",
                            transition: "all 0.2s ease-in-out",
                            mb: 2,
                            "&:hover": {
                              borderColor: "#0A507D",
                              bgcolor: isDarkMode ? "#333333" : "#f8f9fa",
                            },
                          }}
                        >
                          <Button
                            variant="text"
                            component="label"
                            fullWidth
                            disabled={isUploading || fetchingAttachments}
                            sx={{
                              height: 100,
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                              color: isDarkMode ? "#b0bec5" : "#666666",
                              "&:hover": {
                                bgcolor: "transparent",
                                color: "#0A507D",
                              },
                            }}
                          >
                            {isUploading || fetchingAttachments ? (
                              <CircularProgress
                                size={32}
                                sx={{ opacity: 0.7 }}
                              />
                            ) : (
                              <CloudUploadIcon
                                sx={{ fontSize: 32, opacity: 0.7 }}
                              />
                            )}
                            <Typography
                              variant="body2"
                              sx={{ fontWeight: 500 }}
                            >
                              {fetchingAttachments
                                ? "Loading attachments..."
                                : isUploading
                                ? "Uploading files..."
                                : "Click to upload files or drag and drop"}
                            </Typography>
                            <Typography variant="caption" sx={{ opacity: 0.7 }}>
                              PDF, DOC, DOCX, JPG, PNG, TXT (Max 2MB each)
                            </Typography>
                            <input
                              type="file"
                              hidden
                              multiple
                              accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.txt"
                              onChange={handleFileChange}
                            />
                          </Button>
                        </Paper>

                        {/* Display Uploaded Attachments */}
                        {uploadedAttachments.length > 0 && (
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                mb: 2,
                                color: isDarkMode ? "#b0bec5" : "#666666",
                                fontWeight: 500,
                              }}
                            >
                              Files ({uploadedAttachments.length})
                            </Typography>
                            <Grid container spacing={2}>
                              {uploadedAttachments.map((attachment) => (
                                <Grid item xs={12} key={attachment.id}>
                                  <Paper
                                    elevation={0}
                                    sx={{
                                      p: 3,
                                      bgcolor: attachment.uploadError
                                        ? isDarkMode
                                          ? "#3d1f1f"
                                          : "#ffebee"
                                        : isDarkMode
                                        ? "#333333"
                                        : "#e8f4fd",
                                      border: `1px solid ${
                                        attachment.uploadError
                                          ? isDarkMode
                                            ? "#f44336"
                                            : "#f44336"
                                          : isDarkMode
                                          ? "#404040"
                                          : "#0A507D"
                                      }`,
                                      borderRadius: 2,
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 2,
                                          flex: 1,
                                        }}
                                      >
                                        <Box sx={{ fontSize: 24 }}>
                                          {getFileIcon(
                                            attachment.fileName,
                                            attachment.fileType
                                          )}
                                        </Box>
                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                          <Typography
                                            variant="body2"
                                            sx={{
                                              fontWeight: 500,
                                              overflow: "hidden",
                                              textOverflow: "ellipsis",
                                              whiteSpace: "nowrap",
                                              color: isDarkMode
                                                ? "#ffffff"
                                                : "#333333",
                                              cursor: attachment.isExisting
                                                ? "pointer"
                                                : "default",
                                            }}
                                            onClick={() =>
                                              attachment.isExisting &&
                                              handleFileAction(attachment)
                                            }
                                          >
                                            {attachment.fileName}
                                          </Typography>
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: isDarkMode
                                                ? "#b0bec5"
                                                : "#666666",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: 1,
                                            }}
                                          >
                                            {formatFileSize(
                                              attachment.fileSize
                                            )}
                                            {attachment.uploadError ? (
                                              " ❌ Upload failed"
                                            ) : attachment.isUploaded ? (
                                              <CheckCircleIcon
                                                sx={{
                                                  fontSize: 14,
                                                  color: "#28a745",
                                                }}
                                              />
                                            ) : attachment.isUploading ? (
                                              " ⏳ Uploading..."
                                            ) : (
                                              " 📎 Ready"
                                            )}
                                            {attachment.uploadError
                                              ? attachment.uploadError
                                              : attachment.isUploaded
                                              ? " Uploaded successfully"
                                              : attachment.isUploading
                                              ? " Uploading to server"
                                              : " Ready to upload"}
                                          </Typography>
                                        </Box>
                                      </Box>
                                      <Box sx={{ display: "flex", gap: 1 }}>
                                        <IconButton
                                          size="small"
                                          onClick={() =>
                                            handleDeleteConfirmation(attachment)
                                          }
                                          disabled={
                                            attachment.isUploading ||
                                            deleteConfirmDialog.isDeleting
                                          }
                                          sx={{
                                            color: "#f44336",
                                            "&:hover": {
                                              bgcolor: "rgba(244, 67, 54, 0.1)",
                                            },
                                          }}
                                          title="Delete attachment"
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </Box>
                                    </Box>

                                    {attachment.isUploading &&
                                      uploadProgress[attachment.id] !==
                                        undefined && (
                                        <Box sx={{ mt: 1 }}>
                                          <LinearProgress
                                            variant="determinate"
                                            value={
                                              uploadProgress[attachment.id]
                                            }
                                            sx={{
                                              height: 6,
                                              borderRadius: 3,
                                              bgcolor: isDarkMode
                                                ? "#404040"
                                                : "#e0e0e0",
                                            }}
                                          />
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              color: isDarkMode
                                                ? "#b0bec5"
                                                : "#666666",
                                              mt: 0.5,
                                              display: "block",
                                            }}
                                          >
                                            {uploadProgress[attachment.id]}%
                                            uploaded
                                          </Typography>
                                        </Box>
                                      )}
                                  </Paper>
                                </Grid>
                              ))}
                            </Grid>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  )}
                  {ticketTab === 1 && (
                    <Box>
                      {loading && (
                        <Typography fontSize="0.95rem">Loading...</Typography>
                      )}
                      {error && (
                        <Typography fontSize="0.95rem" color="error">
                          {error}
                        </Typography>
                      )}
                      {!loading && !error && ticketHistory.length === 0 && (
                        <Typography fontSize="0.95rem">
                          No ticket history found.
                        </Typography>
                      )}
                      {!loading &&
                        !error &&
                        ticketHistory.map((ticket) => (
                          <Paper
                            key={ticket.staff_meting_id}
                            variant="outlined"
                            sx={{
                              mb: 2,
                              p: 1.2,
                              borderRadius: 1.2,
                              boxShadow: "0 1px 4px 0 rgba(80,160,100,0.05)",
                              border: "1px solid #edeef0",
                              bgcolor: "#fff",
                            }}
                          >
                            {/* TOP ROW: Subject and status */}
                            <Box
                              display="flex"
                              alignItems="center"
                              justifyContent="space-between"
                              mb={0.2}
                            >
                              <Typography fontWeight={500} fontSize="0.8rem">
                                {ticket.subjects}
                              </Typography>
                              <Box
                                sx={{
                                  px: 1.2,
                                  py: 0.1,
                                  borderRadius: 1,
                                  bgcolor: "#e6f8ed",
                                  color: "#04914d",
                                  fontWeight: 700,
                                  fontSize: "0.77rem",
                                  display: "flex",
                                  alignItems: "center",
                                }}
                              >
                                ✔️&nbsp;RESOLVED
                              </Box>
                            </Box>

                            {/* DESCRIPTION */}
                            

                            {/* RESOLUTION */}
                            <Box
                              sx={{
                                bgcolor: "#e6f8ed",
                                color: "#04914d",
                                p: "7px 9px",
                                borderRadius: 1,
                                mb: 1,
                                fontWeight: 500,
                                fontSize: "0.7rem",
                              }}
                            >
                              <Box component="span" sx={{ fontWeight: 700 }}>
                                Resolution:
                              </Box>{" "}
                              {ticket.closing_message || "N/A"}
                            </Box>

                            {/* FOOTER: Created & Resolved */}
                            <Box
                              display="flex"
                              alignItems="center"
                              gap={3}
                              mt={0.2}
                              flexWrap="wrap"
                            >
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <ScheduleIcon
                                  sx={{ fontSize: 15, color: "#b6b6b6" }}
                                />
                                <Typography
                                  variant="caption"
                                  color="grey.700"
                                  fontSize="0.82rem"
                                >
                                  Created:{" "}
                                  {new Date(ticket.created_at).toLocaleString()}
                                </Typography>
                              </Box>
                              <Box display="flex" alignItems="center" gap={0.5}>
                                <CheckCircleIcon
                                  sx={{ fontSize: 15, color: "#04914d" }}
                                />
                                <Typography
                                  variant="caption"
                                  color="grey.700"
                                  fontSize="0.82rem"
                                >
                                  Resolved:{" "}
                                  {new Date(ticket.updated_at).toLocaleString()}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        ))}
                    </Box>
                  )}
                </Box>
              ) : null}
            </>
          )}

          {/* CHAT TAB - WITH CORRECTED LABELS */}
          {activeTab === 1 && (
            <Box
              sx={{ display: "flex", flexDirection: "column", height: "100%" }}
            >
              {isLoadingMessages ? (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <CircularProgress size={32} />
                  <Typography
                    sx={{
                      ml: 2,
                      color: isDarkMode ? "#b0bec5" : "#666",
                      fontSize: "0.875rem",
                    }}
                  >
                    Loading messages...
                  </Typography>
                </Box>
              ) : messages.length === 0 ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    flex: 1,
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 64,
                      height: 64,
                      borderRadius: "50%",
                      bgcolor: isDarkMode ? "#2d2d2d" : "#e3f2fd",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <PersonIcon sx={{ fontSize: 32, color: "#0A507D" }} />
                  </Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      color: isDarkMode ? "#b0bec5" : "#666",
                      fontWeight: 600,
                      fontSize: "0.95rem",
                    }}
                  >
                    No messages yet
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#808080" : "#999",
                      textAlign: "center",
                      fontSize: "0.8rem",
                    }}
                  >
                    Start the conversation
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 0,
                    flex: 1,
                    overflow: "auto",
                  }}
                >
                  {Object.entries(messageGroups).map(([date, dateMessages]) => (
                    <Box key={date}>
                      {/* Date Divider */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                          my: 2,
                          px: 1.5,
                        }}
                      >
                        <Divider
                          sx={{
                            flex: 1,
                            borderColor: isDarkMode ? "#333" : "#e5e7eb",
                          }}
                        />
                        <Typography
                          variant="caption"
                          sx={{
                            color: isDarkMode ? "#666" : "#9ca3af",
                            bgcolor: isDarkMode ? "#121212" : "#f5f7fa",
                            px: 1.5,
                            py: 0.25,
                            borderRadius: 1,
                            fontWeight: 600,
                            fontSize: "0.65rem",
                            textTransform: "uppercase",
                            letterSpacing: "0.5px",
                          }}
                        >
                          {date}
                        </Typography>
                        <Divider
                          sx={{
                            flex: 1,
                            borderColor: isDarkMode ? "#333" : "#e5e7eb",
                          }}
                        />
                      </Box>

                      {/* Messages */}
                      {dateMessages.map((msg, index) => {
                        const senderInfo = getMessageSenderInfo(msg);
                        const isFirstInGroup = index === 0;

                        return (
                          <Paper
                            key={msg.id || index}
                            elevation={0}
                            sx={{
                              mx: 1.5,
                              mb: 0,
                              borderRadius: 0,
                              borderTop: isFirstInGroup
                                ? `1px solid ${
                                    isDarkMode ? "#2d2d2d" : "#e5e7eb"
                                  }`
                                : "none",
                              borderBottom: `1px solid ${
                                isDarkMode ? "#2d2d2d" : "#e5e7eb"
                              }`,
                              bgcolor: isDarkMode ? "#1a1a1a" : "#ffffff",
                              transition: "all 0.15s",
                              "&:hover": {
                                bgcolor: isDarkMode ? "#252525" : "#f9fafb",
                              },
                            }}
                          >
                            <Box sx={{ p: 1.5 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  mb: 0.75,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  <Avatar
                                    sx={{
                                      width: 28,
                                      height: 28,
                                      bgcolor: senderInfo.avatarBg,
                                      fontSize: "0.7rem",
                                      fontWeight: 700,
                                    }}
                                  >
                                    {senderInfo.avatarText}
                                  </Avatar>
                                  <Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 0.75,
                                      }}
                                    >
                                      <Typography
                                        variant="subtitle2"
                                        sx={{
                                          fontWeight: 600,
                                          color: isDarkMode
                                            ? "#ffffff"
                                            : "#1e293b",
                                          fontSize: "0.8rem",
                                        }}
                                      >
                                        {senderInfo.label}
                                      </Typography>
                                      {/* Show badge for EduSkills (is_spoc: 0) */}

                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: isDarkMode
                                            ? "#666"
                                            : "#9ca3af",
                                          fontSize: "0.7rem",
                                          ml: 0.5,
                                        }}
                                      >
                                        {formatTime(msg.created_at)}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                                {msg.sending && (
                                  <CircularProgress
                                    size={12}
                                    sx={{ color: "#9ca3af" }}
                                  />
                                )}
                                {!msg.sending &&
                                  senderInfo.isSpoc &&
                                  senderInfo.isOwnMessage && (
                                    <CheckCircleIcon
                                      sx={{ fontSize: 14, color: "#4ade80" }}
                                    />
                                  )}
                              </Box>
                              <Box sx={{ pl: 4.5 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: isDarkMode ? "#d1d5db" : "#374151",
                                    lineHeight: 1.5,
                                    fontSize: "0.85rem",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-word",
                                  }}
                                >
                                  {msg.message}
                                </Typography>
                              </Box>
                            </Box>
                          </Paper>
                        );
                      })}
                    </Box>
                  ))}
                  <div ref={messagesEndRef} />
                </Box>
              )}

              {/* Chat Input */}
              <Box
                sx={{
                  mt: 1.5,
                  pt: 1.5,
                  px: 1.5,
                  borderTop: `1px solid ${isDarkMode ? "#2d2d2d" : "#e5e7eb"}`,
                  bgcolor: isDarkMode ? "#1a1a1a" : "#ffffff",
                }}
              >
                <Box sx={{ display: "flex", gap: 1, alignItems: "flex-end" }}>
                  <TextField
                    inputRef={chatInputRef}
                    fullWidth
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Type a message..."
                    variant="outlined"
                    size="small"
                    disabled={connectionStatus !== "connected"}
                    multiline
                    maxRows={3}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 1.5,
                        bgcolor: isDarkMode ? "#252525" : "#f9fafb",
                        fontSize: "0.85rem",
                        "& fieldset": {
                          borderColor: isDarkMode ? "#333" : "#e5e7eb",
                        },
                        "&:hover fieldset": {
                          borderColor: "#0A507D",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#0A507D",
                        },
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={
                      !newMessage.trim() ||
                      isSending ||
                      connectionStatus !== "connected"
                    }
                    sx={{
                      bgcolor: "#0A507D",
                      minWidth: 40,
                      height: 40,
                      p: 1,
                      borderRadius: 1.5,
                      "&:hover": { bgcolor: "#083d5f" },
                      "&:disabled": {
                        bgcolor: isDarkMode ? "#333" : "#e5e7eb",
                        color: isDarkMode ? "#666" : "#9ca3af",
                      },
                    }}
                  >
                    {isSending ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <SendIcon sx={{ fontSize: 18 }} />
                    )}
                  </Button>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: 0.75,
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      color:
                        connectionStatus === "connected"
                          ? "#4ade80"
                          : "#ef4444",
                      fontSize: "0.65rem",
                      fontWeight: 600,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 5,
                        height: 5,
                        borderRadius: "50%",
                        bgcolor:
                          connectionStatus === "connected"
                            ? "#4ade80"
                            : "#ef4444",
                      }}
                    />
                    {connectionStatus === "connected"
                      ? "Connected"
                      : "Disconnected"}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{
                      color: isDarkMode ? "#555" : "#9ca3af",
                      fontSize: "0.65rem",
                    }}
                  >
                    Enter to send
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        {/* Footer Actions - Only show on Ticket Tab */}
        {activeTab === 0 && (
          <DialogActions
            sx={{
              p: 4,
              pt: 2,
              bgcolor: isDarkMode ? "#1e1e1e" : "#fafafa",
              gap: 2,
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderRadius: 3,
                px: 3,
                py: 1.5,
                borderColor: isDarkMode ? "#404040" : "#e0e0e0",
                color: isDarkMode ? "#b0bec5" : "#666666",
              }}
            >
              Close
            </Button>

            {ticketTab === 0 &&!fetchingMeetings && !hasPendingMeetings &&  (
             
                <>
              <Button
                onClick={scheduleMeeting}
                variant="contained"
                disabled={
                  meetingLoading || !formData.subject || !formData.description
                }
                startIcon={
                  meetingLoading ? (
                    <CircularProgress size={18} color="inherit" />
                  ) : (
                    <VideoCallIcon />
                  )
                }
                sx={{
                  borderRadius: 3,
                  px: 4,
                  py: 1.5,
                  bgcolor: "#28a745",
                  boxShadow: "0 4px 15px 0 rgba(40, 167, 69, 0.4)",
                  "&:hover": {
                    bgcolor: "#218838",
                    boxShadow: "0 6px 20px 0 rgba(40, 167, 69, 0.6)",
                  },
                  minWidth: 140,
                }}
              >
                {meetingLoading ? "Rising..." : "Rise Ticket"}
              </Button>
              </>

            )}
          </DialogActions>
        )}
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmDialog.open}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: isDarkMode ? "#1e1e1e" : "#ffffff",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            pb: 2,
            color: isDarkMode ? "#ffffff" : "#333333",
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: "rgba(244, 67, 54, 0.1)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <WarningIcon sx={{ color: "#f44336", fontSize: 24 }} />
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Delete Attachment
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pb: 2 }}>
          <Typography
            variant="body1"
            sx={{ color: isDarkMode ? "#b0bec5" : "#666666", mb: 2 }}
          >
            Are you sure you want to delete this attachment? This action cannot
            be undone.
          </Typography>

          {deleteConfirmDialog.attachment && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2,
                bgcolor: isDarkMode ? "#2d2d2d" : "#f5f5f5",
                borderRadius: 2,
                border: `1px solid ${isDarkMode ? "#404040" : "#e0e0e0"}`,
              }}
            >
              <Box sx={{ fontSize: 20 }}>
                {getFileIcon(
                  deleteConfirmDialog.attachment.fileName,
                  deleteConfirmDialog.attachment.fileType
                )}
              </Box>
              <Box>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    color: isDarkMode ? "#ffffff" : "#333333",
                  }}
                >
                  {deleteConfirmDialog.attachment.fileName}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: isDarkMode ? "#b0bec5" : "#666666" }}
                >
                  {formatFileSize(deleteConfirmDialog.attachment.fileSize)}
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, pt: 1, gap: 2 }}>
          <Button
            onClick={handleCancelDelete}
            variant="outlined"
            disabled={deleteConfirmDialog.isDeleting}
            sx={{
              borderRadius: 2,
              px: 3,
              borderColor: isDarkMode ? "#404040" : "#e0e0e0",
              color: isDarkMode ? "#b0bec5" : "#666666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            disabled={deleteConfirmDialog.isDeleting}
            startIcon={
              deleteConfirmDialog.isDeleting ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
            sx={{
              borderRadius: 2,
              px: 3,
              bgcolor: "#f44336",
              "&:hover": { bgcolor: "#d32f2f" },
            }}
          >
            {deleteConfirmDialog.isDeleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TicketModal;
